import axios from 'axios';
import {
  AdminRole,
  AdminUser,
  CallOutcome,
  CrmRdv,
  CrmStats,
  CrmUser,
  DailyCallStats,
  JpoRsvpStats,
  JpoRsvpSubmission,
  PostbacSubmission,
  PreinscriptionSubmission,
  QueueResponse,
  VisiteDetail,
} from './types';

const api = axios.create({
  withCredentials: true,
});

interface ApiError {
  field: string;
  message: string;
}

interface AuthResponse {
  success: true;
  user: AdminUser;
}

interface ListResponse<T> {
  success: true;
  data: T[];
}

export function getErrorMessage(error: unknown): string {
  if (axios.isAxiosError(error)) {
    const errors = error.response?.data?.errors as ApiError[] | undefined;
    if (errors?.length) return errors[0].message;
  }
  return 'Une erreur est survenue.';
}

export async function loginAdmin(email: string, password: string): Promise<AdminUser> {
  const response = await api.post<AuthResponse>('/api/admin/auth/login', { email, password });
  return response.data.user;
}

export async function logoutAdmin(): Promise<void> {
  await api.post('/api/admin/auth/logout');
}

export async function getCurrentAdmin(): Promise<AdminUser> {
  const response = await api.get<AuthResponse>('/api/admin/auth/me');
  return response.data.user;
}

export async function getPreinscriptions(): Promise<PreinscriptionSubmission[]> {
  const response = await api.get<ListResponse<PreinscriptionSubmission>>('/api/admin/visites');
  return response.data.data;
}

interface JpoRsvpsResponse {
  success: true;
  stats: JpoRsvpStats;
  data: JpoRsvpSubmission[];
}

export async function getJpoRsvps(): Promise<{ stats: JpoRsvpStats; data: JpoRsvpSubmission[] }> {
  const response = await api.get<JpoRsvpsResponse>('/api/admin/jpo-rsvps');
  return { stats: response.data.stats, data: response.data.data };
}

export async function patchJpoRsvpStatus(id: string, status: string): Promise<JpoRsvpSubmission> {
  const response = await api.patch<{ success: true; data: JpoRsvpSubmission }>(
    `/api/admin/jpo-rsvps/${id}/status`,
    { status }
  );
  return response.data.data;
}

export async function getPostbacLeads(): Promise<PostbacSubmission[]> {
  const response = await api.get<ListResponse<PostbacSubmission>>('/api/admin/postbac');
  return response.data.data;
}

export async function deletePreinscription(id: string): Promise<void> {
  await api.delete(`/api/admin/visites/${id}`);
}

export async function deleteJpoRsvp(id: string): Promise<void> {
  await api.delete(`/api/admin/jpo-rsvps/${id}`);
}

export async function deletePostbacLead(id: string): Promise<void> {
  await api.delete(`/api/admin/postbac/${id}`);
}

// ── Mini-CRM d'appels ────────────────────────────────────────────────────────

export async function updateVisiteStatus(
  id: string,
  payload: { status: string; rdvDate?: string | null; rdvHeure?: string | null }
): Promise<PreinscriptionSubmission> {
  const response = await api.patch<{ success: true; data: PreinscriptionSubmission }>(
    `/api/admin/visites/${id}/status`,
    payload
  );
  return response.data.data;
}

export async function getQueue(limit = 20, offset = 0): Promise<QueueResponse> {
  const response = await api.get<{ success: true } & QueueResponse>(
    `/api/admin/crm/queue?limit=${limit}&offset=${offset}`
  );
  const { counts, items, limit: l, offset: o } = response.data;
  return { counts, items, limit: l, offset: o };
}

// RDV calendar feed. `from`/`to` are YYYY-MM-DD (inclusive); both roles see all.
export async function getRdvs(from: string, to: string): Promise<CrmRdv[]> {
  const response = await api.get<ListResponse<CrmRdv>>(
    `/api/admin/crm/rdvs?from=${from}&to=${to}`
  );
  return response.data.data;
}

export async function getVisiteDetail(id: string): Promise<VisiteDetail> {
  const response = await api.get<{ success: true; data: VisiteDetail }>(
    `/api/admin/crm/visites/${id}`
  );
  return response.data.data;
}

export async function claimLead(id: string): Promise<PreinscriptionSubmission> {
  const response = await api.post<{ success: true; data: PreinscriptionSubmission }>(
    `/api/admin/crm/visites/${id}/claim`
  );
  return response.data.data;
}

export async function releaseLead(id: string): Promise<void> {
  await api.post(`/api/admin/crm/visites/${id}/release`);
}

export interface OutcomePayload {
  outcome: CallOutcome;
  note?: string;
  callbackAt?: string;
  rdvDate?: string;
  rdvHeure?: string;
}

export async function logOutcome(id: string, payload: OutcomePayload): Promise<PreinscriptionSubmission> {
  const response = await api.post<{ success: true; data: PreinscriptionSubmission }>(
    `/api/admin/crm/visites/${id}/outcome`,
    payload
  );
  return response.data.data;
}

export async function updateContact(
  id: string,
  payload: { parentName?: string; phone?: string }
): Promise<PreinscriptionSubmission> {
  const response = await api.patch<{ success: true; data: PreinscriptionSubmission }>(
    `/api/admin/crm/visites/${id}/contact`,
    payload
  );
  return response.data.data;
}

export async function getCrmStats(period: 'today' | '7d'): Promise<CrmStats> {
  const response = await api.get<{ success: true } & CrmStats>(
    `/api/admin/crm/stats?period=${period}`
  );
  const { period: p, rows, totals } = response.data;
  return { period: p, rows, totals };
}

export async function getCrmDailyStats(): Promise<DailyCallStats> {
  const response = await api.get<{ success: true } & DailyCallStats>('/api/admin/crm/stats/daily');
  const { days, agents } = response.data;
  return { days, agents };
}

export async function listUsers(): Promise<CrmUser[]> {
  const response = await api.get<{ success: true; data: CrmUser[] }>('/api/admin/crm/users');
  return response.data.data;
}

export async function createUser(payload: {
  email: string;
  name?: string;
  password: string;
  role: AdminRole;
  niveaux?: string[];
}): Promise<CrmUser> {
  const response = await api.post<{ success: true; data: CrmUser }>('/api/admin/crm/users', payload);
  return response.data.data;
}

export async function updateUser(
  id: string,
  payload: { name?: string | null; role?: AdminRole; isActive?: boolean; password?: string; niveaux?: string[] }
): Promise<CrmUser> {
  const response = await api.patch<{ success: true; data: CrmUser }>(
    `/api/admin/crm/users/${id}`,
    payload
  );
  return response.data.data;
}
